/**
 * Dutch display formatting.
 *
 * Planning values are LocalMinutes (wall-clock, no zone), so they are formatted
 * with UTC getters (exactly like shared/time.ts formatLocal) and never through
 * the browser's time zone. Audit/system timestamps are real instants (ISO with
 * "Z") and are shown in Europe/Amsterdam.
 */
import {
  PLANNING_TIME_ZONE,
  dayOf,
  isoWeek,
  minuteOfDay,
  startOfDay,
  weekdayOfDay,
  type DayIndex,
  type LocalMinutes,
} from '../../shared/time.js';

export const WEEKDAYS_SHORT = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'] as const;
export const WEEKDAYS_LONG = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'] as const;
export const MONTHS_SHORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'] as const;
export const MONTHS_LONG = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
] as const;

const pad2 = (n: number) => String(n).padStart(2, '0');

interface Parts {
  year: number;
  month: number; // 0-based
  date: number;
  weekday: number; // 0 = Sunday
}

function partsOfDay(day: DayIndex): Parts {
  const d = new Date(startOfDay(day) * 60000);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate(), weekday: weekdayOfDay(day) };
}

/** "ma 12 jan" */
export function fmtDayShort(day: DayIndex): string {
  const p = partsOfDay(day);
  return `${WEEKDAYS_SHORT[p.weekday]} ${p.date} ${MONTHS_SHORT[p.month]}`;
}

/** "ma 12 jan 2027" (year only when it differs from `refYear`). */
export function fmtDayShortYear(day: DayIndex, refYear?: number): string {
  const p = partsOfDay(day);
  const base = `${WEEKDAYS_SHORT[p.weekday]} ${p.date} ${MONTHS_SHORT[p.month]}`;
  return refYear === p.year ? base : `${base} ${p.year}`;
}

/** "maandag 12 januari 2027" */
export function fmtDayLong(day: DayIndex): string {
  const p = partsOfDay(day);
  return `${WEEKDAYS_LONG[p.weekday]} ${p.date} ${MONTHS_LONG[p.month]} ${p.year}`;
}

/** "12 jan" */
export function fmtDayMonth(day: DayIndex): string {
  const p = partsOfDay(day);
  return `${p.date} ${MONTHS_SHORT[p.month]}`;
}

/**
 * Short span of days: "4 nov", "3–7 nov", "28 okt – 3 nov". The year is added only when it
 * differs from `refYear` ("29 dec 2026 – 2 jan 2027").
 */
export function fmtDaySpan(first: DayIndex, last: DayIndex, refYear?: number): string {
  const a = partsOfDay(first);
  const b = partsOfDay(last);
  const year = (p: Parts) => (p.year === refYear ? '' : ` ${p.year}`);
  if (a.year !== b.year) return `${a.date} ${MONTHS_SHORT[a.month]} ${a.year} – ${b.date} ${MONTHS_SHORT[b.month]} ${b.year}`;
  if (a.month !== b.month) return `${a.date} ${MONTHS_SHORT[a.month]} – ${b.date} ${MONTHS_SHORT[b.month]}${year(b)}`;
  if (a.date !== b.date) return `${a.date}–${b.date} ${MONTHS_SHORT[b.month]}${year(b)}`;
  return `${a.date} ${MONTHS_SHORT[a.month]}${year(a)}`;
}

/** "januari 2027" */
export function fmtMonthYear(day: DayIndex): string {
  const p = partsOfDay(day);
  return `${MONTHS_LONG[p.month]} ${p.year}`;
}

/** "12-01-2027" */
export function fmtDate(t: LocalMinutes | null | undefined): string {
  if (t === null || t === undefined) return '–';
  const p = partsOfDay(dayOf(t));
  return `${pad2(p.date)}-${pad2(p.month + 1)}-${p.year}`;
}

/** "08:00". A finish exactly at midnight of the next day can be shown as "24:00" with `endOfDay`. */
export function fmtTime(t: LocalMinutes, endOfDay = false): string {
  const m = minuteOfDay(t);
  if (endOfDay && m === 0) return '24:00';
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

/** "08:00–17:00" */
export function fmtTimeRange(start: LocalMinutes, finish: LocalMinutes): string {
  const sameDayEnd = finish > start && dayOf(finish) === dayOf(start) + 1 && minuteOfDay(finish) === 0;
  return `${fmtTime(start)}–${fmtTime(finish, sameDayEnd)}`;
}

/** "12-01-2027 08:00" */
export function fmtDateTime(t: LocalMinutes | null | undefined): string {
  if (t === null || t === undefined) return '–';
  return `${fmtDate(t)} ${fmtTime(t)}`;
}

/** "week 3" (ISO week). */
export function fmtWeek(day: DayIndex): string {
  return `week ${isoWeek(day).week}`;
}

/** Sticky agenda day header: "ma 12 jan · week 3". */
export function fmtDayHeader(day: DayIndex): string {
  return `${fmtDayShort(day)} · ${fmtWeek(day)}`;
}

/** "vandaag" / "morgen" / "gisteren", or null for other days. */
export function fmtRelativeDay(day: DayIndex, today: DayIndex): string | null {
  const diff = day - today;
  if (diff === 0) return 'vandaag';
  if (diff === 1) return 'morgen';
  if (diff === -1) return 'gisteren';
  return null;
}

/** "12 jan – 3 feb 2027" / "12 – 16 jan 2027" / "28 dec 2026 – 8 jan 2027" for a range of LocalMinutes. */
export function fmtDateRange(start: LocalMinutes | null, finish: LocalMinutes | null): string {
  if (start === null || finish === null) return '–';
  const a = partsOfDay(dayOf(start));
  const b = partsOfDay(lastDayOf(start, finish));
  if (a.year !== b.year) return `${a.date} ${MONTHS_SHORT[a.month]} ${a.year} – ${b.date} ${MONTHS_SHORT[b.month]} ${b.year}`;
  if (a.month !== b.month) return `${a.date} ${MONTHS_SHORT[a.month]} – ${b.date} ${MONTHS_SHORT[b.month]} ${b.year}`;
  if (a.date !== b.date) return `${a.date} – ${b.date} ${MONTHS_SHORT[b.month]} ${b.year}`;
  return `${a.date} ${MONTHS_SHORT[a.month]} ${a.year}`;
}

/** Decimal with a Dutch comma, at most `digits` decimals, trailing zeros removed. */
export function fmtDecimal(n: number, digits = 2): string {
  const f = 10 ** digits;
  const rounded = Math.round(n * f) / f;
  return String(rounded).replace('.', ',');
}

/**
 * Working duration: "1,5 d" for a day or more, "4 u" / "2,5 u" below a day.
 * `minutesPerDay` comes from the planning settings (480 at Transect).
 */
export function fmtDuration(minutes: number, minutesPerDay = 480): string {
  if (minutes === 0) return '0 d';
  if (Math.abs(minutes) < minutesPerDay) return `${fmtDecimal(minutes / 60)} u`;
  return `${fmtDecimal(minutes / minutesPerDay)} d`;
}

/** Parses "2", "1,5", "1.5 d", "4u", "4 uur" into working minutes; null when invalid. */
export function parseDuration(input: string, minutesPerDay = 480): number | null {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*(d|dag|dagen|u|uur|h)?\s*$/i.exec(input);
  if (!m) return null;
  const value = parseFloat(m[1]!.replace(',', '.'));
  const unit = (m[2] ?? 'd').toLowerCase();
  const minutes = unit === 'u' || unit === 'uur' || unit === 'h' ? value * 60 : value * minutesPerDay;
  return Math.round(minutes);
}

const instantFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: PLANNING_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** ISO instant ("2027-01-12T13:03:00Z") -> "12-01-2027 14:03" (Amsterdam time). */
export function fmtInstant(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const parts = instantFormatter.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')}`;
}

/** ISO instant -> "21 okt 2026" (Amsterdam date), with no-break spaces so it never wraps inside. */
export function fmtInstantDate(iso: string | null | undefined): string {
  const text = fmtInstant(iso);
  if (text === '–') return text;
  const [d, m, y] = text.slice(0, 10).split('-');
  return [Number(d), MONTHS_SHORT[Number(m) - 1], y].join(' ');
}

/** "zojuist", "5 min geleden", "3 uur geleden", "2 dagen geleden", else the date. */
export function fmtAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'nooit';
  const d = new Date(iso);
  const sec = Math.round((now.getTime() - d.getTime()) / 1000);
  if (Number.isNaN(sec)) return '–';
  if (sec < 60) return 'zojuist';
  if (sec < 3600) return `${Math.floor(sec / 60)} min geleden`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} uur geleden`;
  if (sec < 86400 * 14) {
    const days = Math.floor(sec / 86400);
    return days === 1 ? '1 dag geleden' : `${days} dagen geleden`;
  }
  return fmtInstant(iso).slice(0, 10);
}

/** "1,2 MB" */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '–';
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${fmtDecimal(v, v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** "3 d 4 u" / "4 u 12 min" / "12 min" */
export function fmtUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} d ${h} u`;
  if (h > 0) return `${h} u ${m} min`;
  return `${m} min`;
}

/** Integer with Dutch thousands separator: 13973 -> "13.973". */
export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('nl-NL');
}

/** Last calendar day of a [start, finish) range (a finish at 00:00 belongs to the previous day). */
export function lastDayOf(start: LocalMinutes, finish: LocalMinutes): DayIndex {
  return finish > start && minuteOfDay(finish) === 0 ? dayOf(finish) - 1 : dayOf(finish);
}
